import { useEffect, useRef, useState } from 'react';

export type BuildInfo = { version: string; commit: string; builtAt: string };

/** The build of the JavaScript running in this tab (P5g, backlog §16.7), set once by vite.config.ts. */
export const CLIENT_BUILD: BuildInfo = __BUILD_INFO__;

/** How often an open tab asks the Worker for its build. */
export const UPDATE_CHECK_MS = 10 * 60_000;

/** « v0.5.0 · 935ca76 · 2 nov. 2026 »; a date it cannot read is left out. */
export function formatVersion(info: BuildInfo, locale: string): string {
  const date = new Date(info.builtAt);
  const day = info.builtAt && !Number.isNaN(date.getTime()) ? new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date) : '';
  return [`v${info.version}`, info.commit, day].filter(Boolean).join(' · ');
}

/** Another commit or version on the server is a new build; the build time alone is not (web and Worker build apart). */
export function isNewer(server: BuildInfo, client: BuildInfo): boolean {
  return server.version !== client.version || server.commit !== client.commit;
}

/** A build as /api/health reports it; anything else (an HTML page, an `unknown` build from a plain `wrangler dev`) is null. */
export function parseBuildInfo(value: unknown): BuildInfo | null {
  if (!value || typeof value !== 'object') return null;
  const { version, commit, builtAt } = value as Record<string, unknown>;
  if (typeof version !== 'string' || !version || version === 'unknown') return null;
  if (typeof commit !== 'string' || !/^[0-9a-f]{4,40}$/.test(commit)) return null;
  if (typeof builtAt !== 'string') return null;
  return { version, commit, builtAt };
}

/** The Worker's build, or null when there is no Worker to ask (local mode, `vite preview`, offline). */
export async function fetchHealth(fetchImpl: typeof fetch = fetch): Promise<BuildInfo | null> {
  try {
    const res = await fetchImpl('/api/health', { cache: 'no-store' });
    if (!res.ok || !res.headers.get('content-type')?.includes('application/json')) return null;
    return parseBuildInfo(await res.json());
  } catch {
    return null;
  }
}

/**
 * The server's build when it differs from this tab's, else null. Asks on opening, when the tab comes back to the
 * foreground and every 10 minutes; never reloads anything itself.
 */
export function useUpdateCheck(check: () => Promise<BuildInfo | null>, client: BuildInfo = CLIENT_BUILD): BuildInfo | null {
  const [newer, setNewer] = useState<BuildInfo | null>(null);
  // The latest `check` without restarting the timer when a caller passes a new function.
  const ask = useRef(check);
  ask.current = check;
  useEffect(() => {
    let live = true;
    const run = () => {
      void ask.current().then((server) => {
        if (live && server && isNewer(server, client)) setNewer((had) => (had?.commit === server.commit && had.version === server.version ? had : server));
      });
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') run();
    };
    run();
    document.addEventListener('visibilitychange', onVisible);
    const timer = setInterval(run, UPDATE_CHECK_MS);
    return () => {
      live = false;
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [client]);
  return newer;
}
